import React, { useState, useEffect, useMemo, useRef } from "react";
import { makeStyles } from "@material-ui/core";
import {
  usePlByProgramStyles,
  PlChartPanel,
  PlMultiSelect,
} from "../pl-by-program/plByProgramTheme";
import {
  getIndirectVersionsAPI,
  getFringeForecastVersionComparisonAPI,
} from "../../../../api/reports-api";
import Loading from "../../../../common-components/Loading";
import { AD_MIDNIGHT, AD_SLATE, AD_MIST } from "../../reportColorPalette";
import { CELL_FG, CELL_FG_MUTED, TABLE_BORDER } from "../../reportTableConfig";

const NAV = AD_MIDNIGHT;
const MUTED = AD_SLATE;
const MUTED_LT = AD_MIST;
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';

const FMONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTHS_LOWER = FMONTHS.map((m) => m.toLowerCase());
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

const LINE_COLORS = [
  "#1E3A5F",
  "#F97316",
  "#10B981",
  "#38BDF8",
  "#8B5CF6",
  "#DC2626",
  "#0EA5E9",
  "#EAB308",
];

function normalizeVersionList(data = []) {
  return data.map((v) =>
    typeof v === "object" && v !== null
      ? (v.name ?? v.label ?? String(v))
      : String(v),
  );
}

function isBudgetVersion(label) {
  return /budget/i.test(String(label));
}

/* ─── Hover/legend ordering: Budget versions first (newest year first),
   then all EAC versions, newest year first and highest EAC number first
   within a year — independent of whatever order the API returns. ─── */
function parseVersionYear(label) {
  const m = String(label).match(/(20\d{2})/);
  return m ? Number(m[1]) : null;
}
function parseEacNumber(label) {
  const m = String(label).match(/eac\s*-?\s*(\d+)/i);
  return m ? Number(m[1]) : null;
}
function versionSortKey(label) {
  return {
    budget: isBudgetVersion(label),
    year: parseVersionYear(label) ?? -Infinity,
    eacNumber: parseEacNumber(label) ?? -Infinity,
  };
}
function compareVersionsForDisplay(a, b) {
  const ka = versionSortKey(a);
  const kb = versionSortKey(b);
  if (ka.budget !== kb.budget) return ka.budget ? -1 : 1;
  if (ka.year !== kb.year) return kb.year - ka.year;
  return kb.eacNumber - ka.eacNumber;
}

/* ─── API filter builder for /reports/indirect/fringe-forecast-version-comparison ─── */
function buildApiFilters(filters = {}, versions = []) {
  const f = {};
  if (filters.year) f.financialYear = String(filters.year);
  if (versions.length) f.versions = versions;
  if (filters.quarter?.length) f.quarter = filters.quarter;
  if (filters.monthName?.length)
    f.month = filters.monthName.map(
      (m) => m.charAt(0).toUpperCase() + m.slice(1, 3).toLowerCase(),
    );
  if (filters.costTypeFringe?.length) f.costTypeFringe = filters.costTypeFringe;
  if (filters.account?.length) f.acctIdndname = filters.account;
  return f;
}

/* Sum a 12-value monthly series into 4 quarterly totals */
function toQuarterlyTotals(monthly) {
  const q = [0, 0, 0, 0];
  monthly.forEach((v, i) => {
    q[Math.floor(i / 3)] += v;
  });
  return q;
}

/* ── Local styles for the Month/Quarter toggle — mirrors ForecastVersionsComparison.js ── */
const useStyles = makeStyles(() => ({
  toggleWrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  toggleLabel: {
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    transition: "color 0.2s",
  },
  toggleTrack: {
    position: "relative",
    width: 44,
    height: 22,
    borderRadius: 11,
    background: MUTED_LT,
    cursor: "pointer",
    flexShrink: 0,
    border: `1px solid ${MUTED}`,
    transition: "background 0.2s, border-color 0.2s",
  },
  toggleThumb: {
    position: "absolute",
    top: 2,
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: NAV,
    boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    transition: "left 0.2s, background 0.2s",
  },
}));

/* ── ViewToggle — Month / Quarter (same control as Forecast Version Comparison) ── */
function ViewToggle({ viewMode, onChange, classes }) {
  const isQuarter = viewMode === "Quarter";
  return (
    <div className={classes.toggleWrap}>
      <span
        className={classes.toggleLabel}
        style={{ color: !isQuarter ? NAV : MUTED }}
      >
        Month
      </span>
      <div
        className={classes.toggleTrack}
        onClick={() => onChange(isQuarter ? "Month" : "Quarter")}
        title="Toggle Month / Quarter"
      >
        <div
          className={classes.toggleThumb}
          style={{ left: isQuarter ? 24 : 2 }}
        />
      </div>
      <span
        className={classes.toggleLabel}
        style={{ color: isQuarter ? NAV : MUTED }}
      >
        Quarter
      </span>
    </div>
  );
}

/* ─── Page component ──────────────────────────────────────────────
   Mirrors pl-by-program/ForecastVersionsComparison.js's UI, layout,
   spacing, Month/Quarter toggle, and Version picker (PlMultiSelect)
   for consistency. Account selection lives in the shared global
   filter bar (GlobalFilterPanel.js's "account" filter, populated
   from /reports/indirect/fringe/accounts) rather than a local
   dropdown here — its selected values arrive via `filters.account`
   and are sent as `accidndname`.
   Selected versions are sent in one call to
   /reports/indirect/fringe-forecast-version-comparison as
   { versions: selectedVersions }; the response returns one row per
   version ({ versionName, jan..dec }) with fringe totals already
   summed across all accounts. */
export default function ForecastVersionComparisonFringe({ filters = {} }) {
  const plClasses = usePlByProgramStyles();
  const viewClasses = useStyles();

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const hasAutoPicked = useRef(false);

  const [viewMode, setViewMode] = useState("Month");
  const [seriesByVersion, setSeriesByVersion] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Fetch available versions; default to the latest open EAC + Budget version */
  useEffect(() => {
    setVersionsLoading(true);
    getIndirectVersionsAPI()
      .then((res) => {
        const data = Array.isArray(res) ? res : res?.data || [];
        const list = normalizeVersionList(data);
        setVersions(list);
        if (!hasAutoPicked.current && list.length) {
          hasAutoPicked.current = true;
          const latestOpen = list.find((v) => !isBudgetVersion(v)) ?? list[0];
          const budget = list.find(isBudgetVersion);
          setSelectedVersions([latestOpen, budget].filter(Boolean));
        }
      })
      .catch(() => {})
      .finally(() => setVersionsLoading(false));
  }, []);

  /* Fetch monthly totals for all selected versions in one call */
  useEffect(() => {
    if (versionsLoading || !selectedVersions.length) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFringeForecastVersionComparisonAPI(
      buildApiFilters(filters, selectedVersions),
    )
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const nextSeries = {};
        rows.forEach((row) => {
          const name = row.versionName;
          if (!name) return;
          nextSeries[name] = MONTHS_LOWER.map((mk) => Number(row[mk]) || 0);
        });
        setSeriesByVersion(nextSeries);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load fringe version comparison data.");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    versionsLoading,
    selectedVersions.join("|"),
    filters?.year,
    (filters?.monthName || []).join("|"),
    (filters?.quarter || []).join("|"),
    (filters?.costTypeFringe || []).join("|"),
    (filters?.account || []).join("|"),
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveVersions =
    selectedVersions.length === 0 ? versions : selectedVersions;

  const chartOption = useMemo(() => {
    const categories = viewMode === "Month" ? FMONTHS : QUARTERS;
    const orderedVersions = [...effectiveVersions].sort(compareVersionsForDisplay);
    const series = orderedVersions.map((v, i) => {
      const monthly = seriesByVersion[v] ?? new Array(12).fill(0);
      const data = viewMode === "Month" ? monthly : toQuarterlyTotals(monthly);
      return {
        name: v,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { width: 2, color: LINE_COLORS[i % LINE_COLORS.length] },
        itemStyle: { color: LINE_COLORS[i % LINE_COLORS.length] },
        data,
      };
    });
    return {
      backgroundColor: "transparent",
      grid: { top: 40, right: 20, bottom: 50, left: 60 },
      tooltip: { trigger: "axis" },
      legend: {
        bottom: 0,
        textStyle: { fontFamily: FONT, fontSize: 11, color: CELL_FG },
      },
      xAxis: {
        type: "category",
        data: categories,
        axisLine: { lineStyle: { color: TABLE_BORDER } },
        axisLabel: { fontFamily: FONT, color: CELL_FG_MUTED },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: "#F1F5F9" } },
        axisLabel: {
          fontFamily: FONT,
          color: CELL_FG_MUTED,
          formatter: (v) =>
            Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : v,
        },
      },
      series,
    };
  }, [effectiveVersions, seriesByVersion, viewMode]);

  const chartTitle = `Total Fringe by EAC (${viewMode === "Month" ? "Monthly" : "Quarterly"} View)`;

  if (loading || versionsLoading) return <Loading />;

  return (
    <div className={plClasses.sectionCard}>
      {/* ── Filter bar — Month/Quarter toggle, Version ── */}
      <div className={plClasses.filterBar}>
        <ViewToggle
          viewMode={viewMode}
          onChange={setViewMode}
          classes={viewClasses}
        />

        <PlMultiSelect
          label="Version"
          options={versions}
          value={selectedVersions}
          onChange={setSelectedVersions}
          classes={plClasses}
        />
      </div>

      {/* ── Chart ── */}
      <div className={plClasses.chartPanelStack}>
        {error ? (
          <div
            style={{
              padding: 24,
              color: "#DC2626",
              fontFamily: FONT,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : (
          <PlChartPanel
            title={chartTitle}
            option={chartOption}
            classes={plClasses}
            panelClassName={plClasses.chartPanelLg}
          />
        )}
      </div>
    </div>
  );
}
